import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar.utils";
import { ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useState } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { SidebarItemWithItems } from "../utils/sidebarLinks";
import { isRouteActive } from "../utils/isRouteActive";

function CollapsibleItem({ item }: { item: SidebarItemWithItems }) {
  const [isHovered, setIsHovered] = useState(false);
  const { open, isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();

  // `item.url` cuenta además de los sub-items: hay grupos cuya propia url es una
  // ruta navegable (`/ventas-b2b` tiene su `index`), y mirando sólo los
  // sub-items —que ahí arrancan en `/ventas-b2b/productos`— parado en esa página
  // no se iluminaba NINGÚN ícono del sidebar.
  const isActive =
    isHovered ||
    isRouteActive(location.pathname, item.url) ||
    item.items.some((subItem) => isRouteActive(location.pathname, subItem.url));

  const iconClassName = `w-6 h-6 ${isActive ? "text-brand-primary" : ""}`;

  const handleSubItemClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Collapsible asChild defaultOpen={isActive} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          {!open ? (
            <HoverCard openDelay={150} closeDelay={150}>
              <HoverCardTrigger asChild>
                <Link to={item.items[0].url} onClick={handleSubItemClick}>
                  <SidebarMenuButton
                    className={isActive ? "text-brand-primary" : ""}
                  >
                    {item.icon && <item.icon className={iconClassName} />}

                    <span>{item.title}</span>

                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </Link>
              </HoverCardTrigger>
              <HoverCardContent
                side="right"
                align="start"
                collisionPadding={8}
                // Altura tope = espacio real disponible hasta el borde del
                // viewport (var que expone Radix), + scroll. Así un módulo con
                // muchas opciones (ej. Veterinaria, 14) nunca se sale de
                // pantalla en monitores chicos: scrollea en vez de cortarse.
                className="ml-2 flex max-h-[var(--radix-hover-card-content-available-height)] w-fit flex-col overflow-y-auto p-0"
              >
                <p className="sticky top-0 z-10 border-b border-border bg-popover px-4 py-2">
                  <strong>{item.title}</strong>
                </p>
                <div className="flex flex-col divide-y divide-border">
                  {item.items?.map((subItem) => (
                    <Link
                      to={subItem.url}
                      className="cursor-pointer whitespace-nowrap px-4 py-2 hover:bg-background-200"
                      key={subItem.url}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      onClick={handleSubItemClick}
                    >
                      {subItem.title}
                    </Link>
                  ))}
                </div>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <SidebarMenuButton className={isActive ? "text-brand-primary" : ""}>
              {item.icon && <item.icon className={iconClassName} />}
              <div className="min-w-24 truncate">
                <span>{item.title}</span>
              </div>
              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items?.map((subItem) => (
              <SidebarMenuSubItem key={subItem.url}>
                <SidebarMenuSubButton asChild>
                  <Link to={subItem.url} onClick={handleSubItemClick}>
                    <span>{subItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

export default CollapsibleItem;
